import { useCallback, useEffect, useRef } from "react";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import {
  applyVideoEditorEdl,
  setVideoEditorSelection,
} from "../../actions/videoEditor";
import { VIDEO_EDITOR_HISTORY_LIMIT } from "./constants";
import { isVideoEditorTypingTarget } from "./videoEditorKeyboard";
import {
  selectVideoEditorEdl,
  selectVideoEditorSelectedClipId,
  selectVideoEditorSelectedClipIds,
} from "./videoEditorSelectors";

const cloneSnapshot = (value) => JSON.parse(JSON.stringify(value));

const getEdlSignature = (edl) =>
  JSON.stringify({
    tracks: edl?.tracks || null,
    clips: edl?.clips || null,
  });

const isUndoHotkey = (event) =>
  (event.ctrlKey || event.metaKey) &&
  !event.shiftKey &&
  !event.altKey &&
  (event.key === "z" || event.key === "Z");

const isRedoHotkey = (event) => {
  if (!(event.ctrlKey || event.metaKey) || event.altKey) {
    return false;
  }

  if (event.key === "y" || event.key === "Y") {
    return true;
  }

  return event.shiftKey && (event.key === "z" || event.key === "Z");
};

const useVideoEditorHistory = ({ enabled = false } = {}) => {
  const dispatch = useDispatchApp();
  const edl = useSelectorApp(selectVideoEditorEdl);
  const selectedClipId = useSelectorApp(selectVideoEditorSelectedClipId);
  const selectedClipIds = useSelectorApp(selectVideoEditorSelectedClipIds);
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const skipNextRef = useRef(false);
  const previousRef = useRef(null);

  useEffect(() => {
    if (!enabled || !edl) {
      undoStackRef.current = [];
      redoStackRef.current = [];
      previousRef.current = null;
      skipNextRef.current = false;
      return;
    }

    const selection = { selectedClipId, selectedClipIds };
    const signature = getEdlSignature(edl);
    if (skipNextRef.current) {
      skipNextRef.current = false;
      previousRef.current = {
        signature,
        edl: cloneSnapshot(edl),
        ...selection,
      };
      return;
    }

    if (!previousRef.current) {
      previousRef.current = {
        signature,
        edl: cloneSnapshot(edl),
        ...selection,
      };
      return;
    }

    if (previousRef.current.signature !== signature) {
      undoStackRef.current.push({
        edl: previousRef.current.edl,
        selectedClipId: previousRef.current.selectedClipId,
        selectedClipIds: previousRef.current.selectedClipIds,
      });
      if (undoStackRef.current.length > VIDEO_EDITOR_HISTORY_LIMIT) {
        undoStackRef.current.shift();
      }
      redoStackRef.current = [];
      previousRef.current = {
        signature,
        edl: cloneSnapshot(edl),
        ...selection,
      };
      return;
    }

    previousRef.current = {
      ...previousRef.current,
      ...selection,
    };
  }, [edl, enabled, selectedClipId, selectedClipIds]);

  const restoreSnapshot = useCallback(
    (snapshot) => {
      if (!snapshot?.edl) {
        return;
      }

      skipNextRef.current = true;
      dispatch(
        applyVideoEditorEdl({
          edl: {
            ...edl,
            ...snapshot.edl,
            playhead: edl?.playhead || 0,
          },
        })
      );
      dispatch(
        setVideoEditorSelection({
          clipIds: snapshot.selectedClipIds ||
            (snapshot.selectedClipId ? [snapshot.selectedClipId] : []),
          clipId: snapshot.selectedClipId || null,
        })
      );
    },
    [dispatch, edl]
  );

  const undoLastAction = useCallback(() => {
    const previous = undoStackRef.current.pop();
    if (!previous || !previousRef.current) {
      return;
    }

    redoStackRef.current.push({
      edl: previousRef.current.edl,
      selectedClipId: previousRef.current.selectedClipId,
      selectedClipIds: previousRef.current.selectedClipIds,
    });
    restoreSnapshot(previous);
  }, [restoreSnapshot]);

  const redoLastAction = useCallback(() => {
    const next = redoStackRef.current.pop();
    if (!next || !previousRef.current) {
      return;
    }

    undoStackRef.current.push({
      edl: previousRef.current.edl,
      selectedClipId: previousRef.current.selectedClipId,
      selectedClipIds: previousRef.current.selectedClipIds,
    });
    restoreSnapshot(next);
  }, [restoreSnapshot]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (isVideoEditorTypingTarget(event.target)) {
        return;
      }

      if (isUndoHotkey(event)) {
        if (!undoStackRef.current.length) {
          return;
        }
        event.preventDefault();
        undoLastAction();
        return;
      }

      if (isRedoHotkey(event)) {
        if (!redoStackRef.current.length) {
          return;
        }
        event.preventDefault();
        redoLastAction();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled, redoLastAction, undoLastAction]);
};

export default useVideoEditorHistory;
